import {
  ContactMethod,
  EligibilityCheckMethod,
  GuardianResponse,
  ManagedCareOrganization,
  NotificationResult,
  PhysicianOrder,
  PhysicianOrderStatus,
} from "@xng/xng-backend";

/**
 * Good Faith Effort Tracking (XLOGS-161). The backend vocabulary is "physician order" and that is kept
 * wherever it is the contract; "good faith effort" is the user-facing name.
 */

/** The filters the grid endpoint accepts. `""` means "no filter" for the three select-backed ones. */
export type GfeFilters = {
  search: string;
  status: PhysicianOrderStatus | "";
  campusId: string | "";
  mco: ManagedCareOrganization | "";
  hideExempt: boolean;
};

export const EMPTY_GFE_FILTERS: GfeFilters = {
  search: "",
  status: "",
  campusId: "",
  mco: "",
  hideExempt: false,
};

/** Which modal is open. One discriminated value rather than a boolean per modal. */
export type GfeActiveModal = "edit" | "delete" | null;

/**
 * The row the user acted on. The grid row is keyed by plan of care, not by order: the order is 1:1 with
 * the plan and may not exist yet, so `planOfCareId` + `studentId` is the only stable address.
 *
 * Medicaid ID, campus and MCO ride along because the edit modal's header shows them and the grid already
 * has them — carrying them costs nothing, where re-fetching would cost a request per modal open.
 */
export type GfeSelectedRow = {
  studentId: string;
  planOfCareId: string;
  studentName: string;
  medicaidId: string | null;
  campus: string | null;
  mco: ManagedCareOrganization | null;
};

/**
 * The edit modal's working copy. Every section is always present so a save sends the whole order: the
 * PUT is a full replace and an omitted section is a cleared section.
 */
export type GfeOrderForm = {
  nativeAmericanExemption: boolean;
  physicianName: string;
  guardianName: string;
  guardianContact: string;
  initialNotification: GfeNotificationForm;
  secondNotification: GfeNotificationForm;
  guardianFirstAttempt: GfeOutreachForm;
  guardianSecondAttempt: GfeOutreachForm;
  eligibilityMethod: EligibilityCheckMethod | "";
  eligibilityDate: Date | null;
  registrationReviewed: Date | null;
  purchasedSoftware: Date | null;
  mco: ManagedCareOrganization | "";
  referralSentTo: string;
  responseReceivedFrom: string;
  authorizationDate: Date | null;
  completedBy: string;
  comments: string;
};

export type GfeNotificationForm = {
  dateSent: Date | null;
  method: ContactMethod | "";
  result: NotificationResult | "";
};

export type GfeOutreachForm = {
  date: Date | null;
  method: ContactMethod | "";
  result: GuardianResponse | "";
};

/** The four documented outreaches, in the order the modal presents them. */
export type GfeEffortKey =
  | "initialNotification"
  | "secondNotification"
  | "guardianFirstAttempt"
  | "guardianSecondAttempt";

export type GfeValidationErrors = Partial<
  Record<GfeEffortKey | "physicianName" | "guardianName" | "guardianContact", string>
>;

export type { PhysicianOrder };
